import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Building2, Clock, Plus } from 'lucide-react';

import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { DataTable } from '@/ui/DataTable';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { MobileCardList, type ItemDoCard } from '@/ui/MobileCardList';
import { PageHeader } from '@/ui/PageHeader';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { MultiSelectCombobox } from '@/ui/base/multi-select-combobox';
import { Tabs, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/ui/base/toggle-group';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { useDres } from '../hooks/useDres';
import {
  APRESENTACAO_DO_STATUS,
  ROTULO_ORIGEM,
  ROTULO_TIPO,
  estadoSla,
  formatarData,
  useBaseDasSolicitacoes,
  usePermissoesDeSolicitacao,
  type EstadoSla,
} from './apresentacao';
import { SeloDeSituacao } from './SeloDeSituacao';
import { useSolicitacoesSme } from './dados';
import { NovaSolicitacaoSmeModal } from './NovaSolicitacaoSmeModal';
import type {
  OrigemSolicitacaoSme,
  SolicitacaoSmeLinha,
  StatusSolicitacaoSme,
  TipoSolicitacaoSme,
} from './tipos';

type Aba = 'TODAS' | 'ABERTA' | 'EM_ANALISE' | 'ATENDIDA' | 'RECUSADA';

const FILTROS_PADRAO = {
  busca: '',
  tipo: '',
  status: '',
  origem: '',
  dres: '',
  periodo: 'ALL',
  periodoData: '',
  periodoDe: '',
  periodoAte: '',
};

const ORDEM_SLA: Record<EstadoSla, number> = { vencido: 0, vencendo: 1, ok: 2, sem_prazo: 3 };

function SeloDeOrigem({ origem }: { origem: OrigemSolicitacaoSme }) {
  const Icone = origem === 'DTP' ? ArrowDownLeft : ArrowUpRight;
  return (
    <Badge variant="outline" className="gap-1 whitespace-nowrap">
      <Icone className="h-3 w-3" aria-hidden="true" />
      {ROTULO_ORIGEM[origem]}
    </Badge>
  );
}

function PrazoDaLinha({ linha }: { linha: SolicitacaoSmeLinha }) {
  if (!linha.prazoDesejado) return <span className="text-muted-foreground">—</span>;
  const texto = formatarData(linha.prazoDesejado);
  const sla = estadoSla(linha.prazoDesejado, linha.status);
  if (sla === 'vencido') {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> {texto}
        <span className="sr-only">(SLA vencido)</span>
      </span>
    );
  }
  if (sla === 'vencendo') {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-warning">
        <Clock className="h-3.5 w-3.5" aria-hidden="true" /> {texto}
        <span className="sr-only">(vence em até 48 h)</span>
      </span>
    );
  }
  return <>{texto}</>;
}

interface SolicitacoesSmeListPageProps {
  /**
   * Fixa o sentido. Sem ele, a tela mostra as duas mãos com filtro de origem —
   * é a variante do portal SME. No Financeiro, `origem="DTP"`.
   */
  origem?: OrigemSolicitacaoSme;
}

function tituloDaLista(origem: OrigemSolicitacaoSme | undefined): string {
  if (origem === 'DTP') return 'Solicitações DTP → SME';
  if (origem === 'SME') return 'Solicitações à DTP';
  return 'Solicitações DTP ↔ SME';
}

/**
 * Solicitações DTP ↔ SME — `SmeDemandasList` do protótipo, que serve as duas
 * cascas: `/sme/demandas` (as duas mãos) e `/operacao/financeiro/solicitacoes-sme`
 * (só DTP → SME). Lista e detalhe ficam na casca em que o usuário entrou.
 *
 * QUEM É SÓ DRE NÃO RECEBE A LISTA. `UsuarioAutenticado` (`GET /eu`) não tem
 * campo de DRE, então não há como filtrar "as solicitações da minha
 * diretoria" — e mostrar as de todas seria pior que não mostrar nenhuma.
 * Mesma saída de `/sme/mensagens` e `/sme/tratativas`: o gate fica num
 * componente fino, antes de qualquer outro hook, para não violar a regra dos
 * hooks quando o conteúdo de verdade (com sua própria lista de hooks) nem
 * chega a montar.
 */
export function SolicitacoesSmeListPage({ origem }: SolicitacoesSmeListPageProps = {}) {
  const permissoes = usePermissoesDeSolicitacao();

  if (permissoes.atuaComoDre) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={tituloDaLista(origem)}
          description="Solicitações formais da SME para a equipe de transporte (DTP)"
        />
        <EmptyState
          icon={Building2}
          title="A lista da sua diretoria ainda não está disponível"
          description="Mostrar só as solicitações da sua diretoria depende de uma consulta que ainda está sendo construída. Até lá a lista não é exibida: mostrar as de todas as diretorias seria pior do que não mostrar nenhuma."
        />
      </div>
    );
  }

  return <Conteudo origem={origem} />;
}

function Conteudo({ origem }: SolicitacoesSmeListPageProps) {
  const navigate = useNavigate();
  const base = useBaseDasSolicitacoes();
  const permissoes = usePermissoesDeSolicitacao();
  const { data: dres } = useDres();

  const [novaAberta, definirNovaAberta] = useState(false);
  // Filtros na barra de endereço, como no protótipo (`useUrlFilters`): o link
  // copiado abre a mesma lista, e voltar do detalhe não perde o recorte.
  const { values: filtros, setFilter, setMany, clearAll } = useUrlFilters(FILTROS_PADRAO);
  const { busca, tipo, status } = filtros;
  const origemFiltrada = (filtros.origem || 'TODAS') as OrigemSolicitacaoSme | 'TODAS';
  const dreIds = useMemo(() => (filtros.dres ? filtros.dres.split(',') : []), [filtros.dres]);
  const periodo: DateRangeValue = {
    preset: (filtros.periodo || 'ALL') as DateRangeValue['preset'],
    date: filtros.periodoData || undefined,
    from: filtros.periodoDe || undefined,
    to: filtros.periodoAte || undefined,
  };

  const origemEfetiva = origem ?? (origemFiltrada === 'TODAS' ? undefined : origemFiltrada);
  const intervalo = resolveDateRange(periodo);
  // A aba e o seletor de status são o mesmo filtro, como no protótipo.
  const statusEfetivo = (status || undefined) as StatusSolicitacaoSme | undefined;

  const { data, isLoading, isError, refetch } = useSolicitacoesSme({
    busca: busca || undefined,
    tipo: (tipo || undefined) as TipoSolicitacaoSme | undefined,
    status: statusEfetivo,
    origem: origemEfetiva,
    dreIds: dreIds.length > 0 ? dreIds : undefined,
    criadaDe: intervalo.from,
    criadaAte: intervalo.to,
  });

  const linhas = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => {
        const sa = estadoSla(a.prazoDesejado, a.status);
        const sb = estadoSla(b.prazoDesejado, b.status);
        if (ORDEM_SLA[sa] !== ORDEM_SLA[sb]) return ORDEM_SLA[sa] - ORDEM_SLA[sb];
        if (a.prazoDesejado && b.prazoDesejado)
          return a.prazoDesejado.localeCompare(b.prazoDesejado);
        return b.criadaEm.localeCompare(a.criadaEm);
      }),
    [data],
  );

  const contagens = useMemo(() => {
    let abertas = 0;
    let emAnalise = 0;
    let vencidas = 0;
    let vencendo = 0;
    for (const s of linhas) {
      if (s.status === 'ABERTA') abertas += 1;
      if (s.status === 'EM_ANALISE') emAnalise += 1;
      const sla = estadoSla(s.prazoDesejado, s.status);
      if (sla === 'vencido') vencidas += 1;
      else if (sla === 'vencendo') vencendo += 1;
    }
    return { total: linhas.length, abertas, emAnalise, vencidas, vencendo };
  }, [linhas]);

  const resumo = useMemo(() => {
    const partes: string[] = [];
    if (contagens.vencidas > 0) partes.push(`⚠ ${contagens.vencidas} com SLA vencido`);
    if (contagens.vencendo > 0) partes.push(`${contagens.vencendo} vencendo em 48 h`);
    if (contagens.abertas + contagens.emAnalise > 0) {
      partes.push(
        `${contagens.abertas + contagens.emAnalise} em fila (${contagens.abertas} abertas · ${contagens.emAnalise} em análise)`,
      );
    }
    const inicio =
      partes.length > 0
        ? partes.join(' · ')
        : 'Solicitações formais da SME para a equipe de transporte (DTP)';
    return `${inicio} · Prazo padrão de resposta: 5 dias úteis`;
  }, [contagens]);

  const titulo = tituloDaLista(origem);

  const podeAbrir = origem === 'DTP' || permissoes.podeAbrirPelaSme;
  const origemDaNova = origem ?? permissoes.origemPadrao;

  const camposFiltro: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nº ou título…',
      ariaLabel: 'Buscar por número ou título da demanda',
      flex: true,
    },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select',
      allOptionLabel: 'Todos os tipos',
      width: 'w-full sm:w-48',
      options: Object.entries(ROTULO_TIPO).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      allOptionLabel: 'Todos os status',
      width: 'w-full sm:w-48',
      options: (['ABERTA', 'EM_ANALISE', 'ATENDIDA', 'RECUSADA'] as const).map((s) => ({
        value: s,
        label: APRESENTACAO_DO_STATUS[s].rotulo,
      })),
    },
  ];

  const abrirDetalhe = (linha: SolicitacaoSmeLinha) => navigate(`${base}/${linha.id}`);

  const colunas = useMemo<TableViewColumn<SolicitacaoSmeLinha>[]>(
    () => [
      {
        id: 'numero',
        header: 'Nº',
        cell: (l) => `#${l.numero}`,
        cellClassName: 'font-mono text-xs',
        isCritical: true,
        canHide: false,
      },
      ...(origem
        ? []
        : [
            {
              id: 'origem',
              header: 'Origem',
              cell: (l: SolicitacaoSmeLinha) => <SeloDeOrigem origem={l.origem} />,
              linkedFilterKeys: ['origem'],
            },
          ]),
      { id: 'tipo', header: 'Tipo', cell: (l) => ROTULO_TIPO[l.tipo], linkedFilterKeys: ['tipo'] },
      { id: 'titulo', header: 'Título', accessorKey: 'titulo', isCritical: true },
      { id: 'dre', header: 'DRE', accessorKey: 'dreNome', linkedFilterKeys: ['dres'] },
      { id: 'escola', header: 'Escola', cell: (l) => l.escolaNome ?? '—' },
      {
        id: 'status',
        header: 'Status',
        cell: (l) => <SeloDeSituacao status={l.status} />,
        isStatusColumn: true,
        linkedFilterKeys: ['status'],
      },
      { id: 'criada', header: 'Criada', cell: (l) => formatarData(l.criadaEm) },
      {
        id: 'prazo',
        header: 'Prazo',
        headerTermo: 'SLA_DEMANDA',
        cell: (l) => <PrazoDaLinha linha={l} />,
      },
    ],
    [origem],
  );

  const tableView = useTableViewPreferences({
    contextKey: origem ? `sme:demandas:${origem.toLowerCase()}` : 'sme:demandas',
    columns: colunas,
    filterValues: filtros,
  });

  const cartoes: ItemDoCard[] = linhas.map((l) => {
    const sla = estadoSla(l.prazoDesejado, l.status);
    return {
      id: l.id,
      title: `#${l.numero} · ${l.titulo}`,
      subtitle: ROTULO_TIPO[l.tipo],
      badges: (
        <div className="flex flex-wrap gap-1">
          <SeloDeOrigem origem={l.origem} />
          <SeloDeSituacao status={l.status} />
          {sla === 'vencido' && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" /> SLA vencido
            </Badge>
          )}
          {sla === 'vencendo' && (
            <Badge variant="outline" className="gap-1 border-warning text-warning">
              <Clock className="h-3 w-3" aria-hidden="true" /> Vence em 48 h
            </Badge>
          )}
        </div>
      ),
      fields: [
        { label: 'DRE', value: l.dreNome },
        { label: 'Escola', value: l.escolaNome ?? '—' },
        { label: 'Criada em', value: formatarData(l.criadaEm) },
        { label: 'Prazo', value: l.prazoDesejado ? formatarData(l.prazoDesejado) : '—' },
      ],
      onClick: () => abrirDetalhe(l),
    };
  });

  return (
    <div className="space-y-4">
      <PageHeader title={titulo} description={resumo}>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          {!origem && (
            <ToggleGroup
              type="single"
              value={origemFiltrada}
              onValueChange={(v) => v && setFilter('origem', v === 'TODAS' ? '' : v)}
              variant="outline"
              size="sm"
              className="flex-wrap justify-start"
              aria-label="Filtrar por origem"
            >
              <ToggleGroupItem value="TODAS">Todas as origens</ToggleGroupItem>
              <ToggleGroupItem value="SME">
                <ArrowUpRight className="mr-1 h-3 w-3" aria-hidden="true" /> SME → DTP
              </ToggleGroupItem>
              <ToggleGroupItem value="DTP">
                <ArrowDownLeft className="mr-1 h-3 w-3" aria-hidden="true" /> DTP → SME
              </ToggleGroupItem>
            </ToggleGroup>
          )}
          {podeAbrir && (
            <Button onClick={() => definirNovaAberta(true)} className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" /> Nova solicitação
            </Button>
          )}
        </div>
      </PageHeader>

      {podeAbrir && (
        <NovaSolicitacaoSmeModal
          open={novaAberta}
          onOpenChange={definirNovaAberta}
          origem={origemDaNova}
        />
      )}

      <Tabs
        value={(status || 'TODAS') as Aba}
        onValueChange={(v) => setFilter('status', v === 'TODAS' ? '' : v)}
      >
        <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
          <TabsTrigger value="TODAS">Todas ({contagens.total})</TabsTrigger>
          <TabsTrigger value="ABERTA">Abertas ({contagens.abertas})</TabsTrigger>
          <TabsTrigger value="EM_ANALISE">Em análise ({contagens.emAnalise})</TabsTrigger>
          <TabsTrigger value="ATENDIDA">Atendidas</TabsTrigger>
          <TabsTrigger value="RECUSADA">Recusadas</TabsTrigger>
        </TabsList>
      </Tabs>

      <FilterBar
        fields={camposFiltro}
        values={{ busca, tipo, status }}
        onChange={setFilter}
        onClear={clearAll}
        defaultValues={{ busca: '', tipo: '', status: '' }}
      >
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">DRE</Label>
          <MultiSelectCombobox
            options={(dres ?? []).map((d) => ({
              value: d.id,
              label: d.nome ?? d.codigo ?? d.id,
            }))}
            selected={dreIds}
            onChange={(ids) => setFilter('dres', ids.join(','))}
            placeholder="Todas as DREs"
            searchPlaceholder="Buscar DRE…"
            emptyMessage="Nenhuma DRE encontrada."
            renderSelectedText={(n) => `${n} DRE${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
            triggerClassName="sm:w-[220px]"
            ariaLabel="Filtrar por DRE"
          />
        </div>
        <DateRangePresetFilter
          label="Criada em"
          value={periodo}
          onChange={(v) =>
            setMany({
              periodo: v.preset,
              periodoData: v.date ?? '',
              periodoDe: v.from ?? '',
              periodoAte: v.to ?? '',
            })
          }
          width="w-full sm:w-[200px]"
        />
      </FilterBar>

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : (
        <>
          <div className="sm:hidden">
            <MobileCardList
              items={cartoes}
              isLoading={isLoading}
              emptyTitle="Nenhuma demanda"
              emptyDescription="Registre a primeira solicitação para a DTP."
            />
          </div>
          <div className="hidden sm:block">
            <DataTable
              columns={tableView.orderedColumns}
              personalizacao={tableView}
              data={linhas}
              isLoading={isLoading}
              onRowClick={abrirDetalhe}
              getRowClassName={(l) => {
                const sla = estadoSla(l.prazoDesejado, l.status);
                if (sla === 'vencido') return 'bg-destructive/5 hover:bg-destructive/10';
                if (sla === 'vencendo') return 'bg-warning/5 hover:bg-warning/10';
                return undefined;
              }}
              emptyTitle="Nenhuma demanda"
              emptyDescription="Registre a primeira solicitação para a DTP."
              emptyActionLabel={podeAbrir ? 'Nova demanda' : undefined}
              onEmptyAction={podeAbrir ? () => definirNovaAberta(true) : undefined}
              ariaLabel={titulo}
            />
          </div>
        </>
      )}

      <div className="flex flex-wrap gap-3 border-t border-border pt-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-destructive" aria-hidden="true" />{' '}
          SLA vencido
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-warning" aria-hidden="true" /> Vence
          em até 48 h
        </span>
        <span>
          · Termos sublinhados (<GlossaryTerm termo="DEMANDA_SME">Demanda SME</GlossaryTerm>,{' '}
          <GlossaryTerm termo="SLA_DEMANDA">Prazo</GlossaryTerm>) abrem definição.
        </span>
      </div>
    </div>
  );
}

export default SolicitacoesSmeListPage;
